import useIsOnPage from 'app/hooks/useIsOnPage';
import useStorageState from 'app/hooks/useStorageState';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import { useAuth } from 'context';
import moment from 'moment';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DAYS_TO_HIDE = 60;
const STORAGE_KEY_PREFIX = 'set_user_names_dialog_closed_at_';

const PAGES_REQUIRING_NAME = [
  '/access/share-equipment',
  '/access/create-fleet',
  '/access/share-fleet',
];

export default function useShouldShowSetUserNamesDialog() {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnNameRequiredPage = useIsOnPage(PAGES_REQUIRING_NAME);

  const { isAuthenticated } = useAuth();
  const { data: user } = useGetUserInfoQuery();

  const hasAllRequiredNames = !!user?.first_name && !!user?.last_name;
  const isUserInfoReceived = !!user && isAuthenticated;

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const storageKey = user?.email
    ? `${STORAGE_KEY_PREFIX}${user.email}`
    : `${STORAGE_KEY_PREFIX}guest`;

  const [lastClosedAt, setLastClosedAt] = useStorageState<number>(
    localStorage,
    storageKey,
    {
      isValid: ts => moment(ts).isValid(),
    },
  );

  const dismissedOnPathRef = useRef<string | null>(null);

  const handleDismiss = useCallback(() => {
    dismissedOnPathRef.current = location.pathname;

    setLastClosedAt(new Date().getTime());
    setIsDialogOpen(false);

    if (isOnNameRequiredPage) {
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        navigate('/');
      }
    }
  }, [isOnNameRequiredPage, navigate, setLastClosedAt, location.pathname]);

  const handleSuccess = useCallback(() => {
    setIsDialogOpen(false);
    setLastClosedAt(undefined);
    dismissedOnPathRef.current = null;
  }, [setLastClosedAt]);

  useEffect(() => {
    if (!isUserInfoReceived || hasAllRequiredNames) {
      if (isDialogOpen && hasAllRequiredNames) setIsDialogOpen(false);
      return;
    }

    const currentPath = location.pathname;

    if (
      dismissedOnPathRef.current &&
      dismissedOnPathRef.current !== currentPath
    ) {
      dismissedOnPathRef.current = null;
    }

    if (dismissedOnPathRef.current === currentPath) {
      return;
    }

    if (isOnNameRequiredPage) {
      setIsDialogOpen(true);
      return;
    }

    const now = new Date().getTime();

    const isCooldownExpired =
      !lastClosedAt ||
      moment(now).diff(moment(lastClosedAt), 'days') >= DAYS_TO_HIDE;

    if (isCooldownExpired) {
      setIsDialogOpen(true);
    }
  }, [
    isOnNameRequiredPage,
    location.pathname,
    isUserInfoReceived,
    hasAllRequiredNames,
    lastClosedAt,
  ]);

  return {
    isOpen: isDialogOpen,
    onDismiss: handleDismiss,
    onSuccess: handleSuccess,
  };
}
